import { z } from 'zod';
const nonEmptyStringSchema = z.string().trim().min(1);
const isoTimestampSchema = nonEmptyStringSchema;
export const matchAnalysisPhaseSchema = z.enum([
    'pre_match',
    'live',
    'halftime',
    'second_half',
    'full_time',
    'post_match',
]);
export const matchAnalysisLocaleSchema = z.enum(['zh-CN', 'en']).default('zh-CN');
export const matchAnalysisCoverageStatusSchema = z.enum([
    'available',
    'partial',
    'unavailable',
    'requires_backfill',
]);
export const matchAnalysisConfidenceSchema = z.enum([
    'trusted',
    'single_source',
    'partial',
    'stale',
    'unresolved',
]);
export const matchAnalysisRequestedIdentitySchema = z.object({
    matchId: nonEmptyStringSchema.optional(),
    radarEventId: nonEmptyStringSchema.optional(),
    fixtureId: z.coerce.number().int().positive().optional(),
}).refine(value => Boolean(value.matchId || value.radarEventId || value.fixtureId), {
    message: 'At least one match identifier is required',
});
export const matchAnalysisBundleCliArgsSchema = matchAnalysisRequestedIdentitySchema.extend({
    phase: matchAnalysisPhaseSchema.default('post_match'),
    refreshEvents: z.boolean().default(false),
    locale: matchAnalysisLocaleSchema,
    pretty: z.boolean().default(false),
});
export const matchAnalysisSourceRefSchema = z.object({
    provider: nonEmptyStringSchema,
    endpoint: nonEmptyStringSchema,
    rawPayloadId: nonEmptyStringSchema.optional(),
    payloadHash: nonEmptyStringSchema.optional(),
    fetchedAt: isoTimestampSchema.optional(),
});
export const matchAnalysisEventSchema = z.object({
    minute: z.number().int().min(0),
    extraMinute: z.number().int().min(0).nullable(),
    type: z.enum(['goal', 'card', 'substitution', 'var', 'other']),
    detail: nonEmptyStringSchema,
    teamName: nonEmptyStringSchema,
    playerName: nonEmptyStringSchema.nullable(),
    assistName: nonEmptyStringSchema.nullable(),
});
export const matchAnalysisBundleSchema = z.object({
    kind: z.literal('match_analysis_bundle'),
    generatedAt: isoTimestampSchema,
    locale: matchAnalysisLocaleSchema,
    identityResolution: z.object({
        status: z.enum(['resolved', 'ambiguous', 'unresolved']),
        requested: matchAnalysisRequestedIdentitySchema,
        resolved: z.object({
            researchMatchId: nonEmptyStringSchema.nullable(),
            radarEventId: nonEmptyStringSchema.nullable(),
            apiFootballFixtureId: z.number().int().positive().nullable(),
        }),
        confidence: matchAnalysisConfidenceSchema,
        warnings: z.array(nonEmptyStringSchema),
    }),
    match: z.object({
        researchMatchId: nonEmptyStringSchema.nullable(),
        radarEventId: nonEmptyStringSchema.nullable(),
        apiFootballFixtureId: z.number().int().positive().nullable(),
        homeTeam: nonEmptyStringSchema,
        awayTeam: nonEmptyStringSchema,
        competition: nonEmptyStringSchema,
        kickoffAt: isoTimestampSchema.nullable(),
        status: matchAnalysisPhaseSchema,
        minute: z.number().int().min(0).nullable(),
        score: z.object({
            home: z.number().int().min(0).nullable(),
            away: z.number().int().min(0).nullable(),
        }),
    }),
    dataState: z.object({
        phase: matchAnalysisPhaseSchema,
        freshness: z.object({
            eventsRefreshedAt: isoTimestampSchema.nullable(),
            latestRetainedEventsAt: isoTimestampSchema.nullable(),
            stale: z.boolean(),
        }),
        coverage: z.array(z.object({
            group: z.enum([
                'pre_match_baseline',
                'current_match_process',
                'tactical_personnel',
                'optional_market_context',
            ]),
            status: matchAnalysisCoverageStatusSchema,
            source: nonEmptyStringSchema,
        })),
        warnings: z.array(nonEmptyStringSchema),
    }),
    preMatchBaseline: z.object({
        status: matchAnalysisCoverageStatusSchema,
        bullets: z.array(nonEmptyStringSchema),
        sourceRefs: z.array(matchAnalysisSourceRefSchema),
    }),
    matchProcess: z.object({
        status: matchAnalysisCoverageStatusSchema,
        events: z.array(matchAnalysisEventSchema),
        periodStats: z.array(z.object({
            teamName: nonEmptyStringSchema,
            period: nonEmptyStringSchema,
            group: nonEmptyStringSchema,
            label: nonEmptyStringSchema,
            value: z.union([z.string(), z.number(), z.null()]),
        })),
        sourceRefs: z.array(matchAnalysisSourceRefSchema),
    }),
    tacticalPersonnel: z.object({
        status: matchAnalysisCoverageStatusSchema,
        homeFormation: nonEmptyStringSchema.nullable(),
        awayFormation: nonEmptyStringSchema.nullable(),
        notes: z.array(nonEmptyStringSchema),
        sourceRefs: z.array(matchAnalysisSourceRefSchema),
    }),
    projectionInputs: z.object({
        status: matchAnalysisCoverageStatusSchema,
        mainJudgmentInputs: z.array(nonEmptyStringSchema),
        supportingEvidence: z.array(nonEmptyStringSchema),
        counterEvidence: z.array(nonEmptyStringSchema),
        confidenceHints: z.array(nonEmptyStringSchema),
        notEnoughEvidenceReasons: z.array(nonEmptyStringSchema),
        prohibitedOutputs: z.array(z.enum([
            'betting_pick',
            'stake_size',
            'guaranteed_win_probability',
            'scenario_tree_by_default',
        ])),
    }),
    optionalMarketContext: z.object({
        status: matchAnalysisCoverageStatusSchema,
        bullets: z.array(nonEmptyStringSchema),
        sourceRefs: z.array(matchAnalysisSourceRefSchema),
    }).nullable(),
});
